'use client';

import type { AtlasUser } from '@/types/atlas';
import { useCallback, useRef } from 'react';
import { isDevelopment } from '@/lib/env';
import { captureException } from '@/lib/sentry';
const ATLAS_BOOTSTRAP_MAX_RETRIES = 50;
const ATLAS_START_MAX_RETRIES = 150;
const ATLAS_RETRY_MS = 100;
const ATLAS_HMAC_TIMEOUT_MS = 5_000;
const ATLAS_VISITOR_STATUS_CODES = new Set([401, 403]);
type AtlasHmacResponse = {
  hash: string;
  userId: string;
  email?: string;
  name?: string;
};
function isAtlasBootstrapReady(): boolean {
  const atlas = window.Atlas;
  return typeof atlas !== 'undefined' && typeof atlas.call === 'function';
}
function isAtlasChatStarted(): boolean {
  const atlas = window.Atlas;
  if (!isAtlasBootstrapReady()) {
    return false;
  }
  if (atlas.started === true || atlas.chat?.isStarted === true) {
    return true;
  }

  // Full SDK exposes chat helpers once the bundle has replaced the bootstrap stub.
  return typeof atlas.chat?.openWindow === 'function';
}
function waitForCondition(isReady: () => boolean, maxRetries: number, errorMessage: string): Promise<void> {
  return new Promise((resolve, reject) => {
    let retries = 0;
    const check = () => {
      if (isReady()) {
        resolve();
        return;
      }
      if (retries >= maxRetries) {
        reject(new Error(errorMessage));
        return;
      }
      retries += 1;
      setTimeout(check, ATLAS_RETRY_MS);
    };
    check();
  });
}
export function waitForAtlasBootstrap(): Promise<void> {
  return waitForCondition(isAtlasBootstrapReady, ATLAS_BOOTSTRAP_MAX_RETRIES, 'Atlas bootstrap failed to load after 5 seconds');
}
export function waitForAtlasStarted(): Promise<void> {
  return waitForCondition(isAtlasChatStarted, ATLAS_START_MAX_RETRIES, 'Atlas failed to start after 15 seconds');
}

/** @deprecated Use waitForAtlasStarted() or waitForAtlasBootstrap() instead. */
export function waitForAtlas(): Promise<void> {
  return waitForAtlasStarted();
}
async function fetchAtlasHmac(): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), ATLAS_HMAC_TIMEOUT_MS);
  try {
    return await fetch('/api/auth/atlas-hmac', {
      signal: controller.signal
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

// Identify is idempotent per user, but AtlasProvider remounts on every
// PR<->app-shell route transition, so an unguarded call refired the HMAC
// fetch on each navigation (twice per mount under dev Strict Mode). Cache the
// successful identify; failures clear the slot so HelpWidget can retry, and a
// different cacheKey (the authenticated user id) invalidates the cache so an
// auth switch within one SPA session re-identifies.
let identifyAtlasUserPromise: Promise<boolean> | null = null;
let identifiedCacheKey: string | null = null;
export function resetIdentifyAtlasUserForTesting() {
  identifyAtlasUserPromise = null;
  identifiedCacheKey = null;
}
export function identifyAtlasUser(options?: {
  cacheKey?: string | null;
}): Promise<boolean> {
  const cacheKey = options?.cacheKey ?? null;
  if (cacheKey !== null && cacheKey !== identifiedCacheKey) {
    identifyAtlasUserPromise = null;
  }
  if (!identifyAtlasUserPromise) {
    identifiedCacheKey = cacheKey;
    const promise = identifyAtlasUserUncached().then(didIdentify => {
      // Only clear our own slot: a stale in-flight identify must not evict a
      // newer one started for a different user.
      if (!didIdentify && identifyAtlasUserPromise === promise) {
        identifyAtlasUserPromise = null;
      }
      return didIdentify;
    });
    identifyAtlasUserPromise = promise;
  }
  return identifyAtlasUserPromise;
}
async function identifyAtlasUserUncached(): Promise<boolean> {
  try {
    const response = await fetchAtlasHmac();
    if (!response.ok) {
      if (ATLAS_VISITOR_STATUS_CODES.has(response.status)) {
        return false;
      }

      // Don't capture exception in development for missing Atlas configuration
      if (!isDevelopment()) {
        captureException(new Error('Failed to fetch Atlas HMAC'), {
          tags: {
            component: 'atlas-chat'
          },
          extra: {
            status: response.status
          }
        });
      }
      return false;
    }
    const userData = (await response.json()) as AtlasHmacResponse;
    await waitForAtlasBootstrap();
    if (!window.Atlas) {
      captureException(new Error('Atlas not available after loading'), {
        tags: {
          component: 'atlas-chat'
        }
      });
      return false;
    }
    const identifyData: AtlasUser = {
      userId: userData.userId,
      name: userData.name,
      email: userData.email,
      userHash: userData.hash
    };
    window.Atlas.call('identify', identifyData);
    return true;
  } catch (error) {
    if (error instanceof Error && (error.message === 'Atlas bootstrap failed to load after 5 seconds' || error.message === 'Atlas failed to start after 15 seconds')) {
      captureException(error, {
        tags: {
          component: 'atlas-chat'
        }
      });
      return false;
    }
    captureException(error instanceof Error ? error : new Error('Error identifying user with Atlas'), {
      tags: {
        component: 'atlas-chat'
      },
      extra: {
        originalError: error
      }
    });
    return false;
  }
}
export async function openAtlasChat(): Promise<boolean> {
  try {
    await waitForAtlasBootstrap();
    await waitForAtlasStarted();
    const {
      chat
    } = window.Atlas;
    if (typeof chat?.openWindow !== 'function') {
      return false;
    }

    // Identification is the responsibility of AtlasProvider (auth-gated).
    // Calling /api/auth/atlas-hmac here for visitors would hit a 401 and
    // delay/block the chat UI on a non-essential request. Atlas supports
    // anonymous conversations, so just open the window.
    // Atlas docs: use chat.openWindow(), not Atlas.call('openWindow').
    chat.hideBubble();
    chat.openWindow();
    window.Atlas.call('showNewChat');
    return true;
  } catch {
    return false;
  }
}
export function useAtlasChat() {
  const inflightRef = useRef<Promise<boolean> | null>(null);
  const openChat = useCallback(async () => {
    // De-dupe overlapping calls (e.g. double-click) so callers see the same
    // outcome instead of a spurious "failed" result for the second click.
    if (inflightRef.current) {
      return inflightRef.current;
    }
    const promise = openAtlasChat();
    inflightRef.current = promise;
    try {
      return await promise;
    } finally {
      inflightRef.current = null;
    }
  }, []);
  const identifyUser = useCallback(async () => {
    return identifyAtlasUser();
  }, []);
  return {
    openChat,
    identifyUser
  };
}